import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { DoorOpen, Loader2, Clock, FileText, Percent, CreditCard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

const kindIcon = (k) => (k === "hourly" ? <Clock size={12} /> : k === "appointment_pct" ? <Percent size={12} /> : <FileText size={12} />);

export default function MyRentalsCard() {
  const { data, isLoading } = useQuery({ queryKey: ["my-rentals"], queryFn: () => api.get("/enhancer/me/rentals").then((r) => r.data) });
  const [paying, setPaying] = useState(false);

  if (isLoading) return null;
  if (!data?.agreements?.length) return null;

  const payRent = async () => {
    setPaying(true);
    try {
      const { data: res } = await api.post("/enhancer/rentals/pay", { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  return (
    <div className="eyw-card p-5 mb-6" data-testid="my-rentals-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><DoorOpen size={18} className="text-[#144F75]" /> Room rent</h3>
        {data.total_due > 0 && (
          <button onClick={payRent} disabled={paying} data-testid="pay-rent-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {paying ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />} Pay {money(data.total_due)} now
          </button>
        )}
      </div>
      <div className="space-y-4">
        {data.agreements.map((a) => (
          <div key={a.id} data-testid={`my-rental-${a.id}`} className="rounded-xl bg-[#F9F7F3] p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-[#1A242B]">{a.location_name}</p>
              <span className="text-sm text-[#B5794E]">Due {money(a.due_total)}</span>
            </div>
            <p className="text-xs text-[#8FA89B] flex flex-wrap gap-3 mt-1">
              {a.monthly_rent > 0 && <span>{money(a.monthly_rent)}/mo</span>}
              {a.hourly_rate > 0 && <span>{money(a.hourly_rate)}/hr</span>}
              {a.appointment_pct > 0 && <span>{a.appointment_pct}% of appointments</span>}
            </p>
            {a.charges?.length > 0 && (
              <div className="mt-2 space-y-1">
                {a.charges.slice(0, 6).map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-xs text-[#4A5D6B]">
                    <span className="flex items-center gap-1.5">{kindIcon(c.kind)} {c.note || c.kind} {c.period && `· ${c.period}`}</span>
                    <span className={c.status === "paid" ? "text-[#5C8A72]" : "text-[#B5794E]"}>{money(c.amount)} · {c.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
